import { MAX_SELECTOR_DATA } from '@app/@core/constants/material-custom.constants';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { CustomValidatorsService } from '@app/@shared/custom-validators/custom-validators.service';
import { LoaderGenericService } from '@app/@shared/loader-generic/loader-generic.service';
import { ControlPanelStore } from '@app/control-panel/control-panel-service/control-panel.store';
import { AgentType } from '@app/control-panel/models/control-panel.model';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import * as _moment from 'moment';
import { Moment } from 'moment';
import { CombosStore } from '@app/requests/combos-service/combos.store';
import { Credentials } from '@app/@core/models/login-models';
import {
  BUSINESS_DIRECTOR,
  CHAIN,
  MACRO_CHAIN,
  MACRO_OFFICE,
  OFFICE_DIRECTOR,
} from '@app/@core/constants/users.constants';
import { TranslateService } from '@ngx-translate/core';
import { DatepickerUtilsService } from '@app/@shared/services/utils/datepicker-utils.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LOCALE_ES } from '@app/@core/constants/common.constants';

const moment = _moment;

@UntilDestroy()
@Component({
  selector: 'scf-invest-form',
  templateUrl: './invest-form.component.html',
  styleUrls: ['./invest-form.component.scss'],
})
export class InvestFormComponent implements OnInit {
  @Input() isDetailAvailable: boolean = true;
  @Input() title: string = 'Title';
  @Input() subtitle: string = 'Subtitle';
  @Output() consultEvent = new EventEmitter<any>();

  maxDate: Date = new Date();
  maxFinalDate: Date;
  minDate: Date;

  investorForm: FormGroup;
  divisionList: CommonDataDTO[] = [];
  agentsReportList: CommonDataDTO[] = [];
  nifcifList: CommonDataDTO[] = [];
  agentList: CommonDataDTO[] = [];
  agentTypeList: CommonDataDTO[] = [];
  radioButtonValues: string[] = ['group', 'nifcif', 'agent'];
  detailsList: CommonDataDTO[] = [];

  userInfo: Credentials;
  macroChainList: CommonDataDTO[] = [];
  macroOfficeList: CommonDataDTO[] = [];

  isMacroOffices: boolean = false;
  isMacroChains: boolean = false;
  isBussinesDirector: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private controlPanelStore: ControlPanelStore,
    private loaderGenericService: LoaderGenericService,
    private customValidators: CustomValidatorsService,
    private combosStore: CombosStore,
    private translateService: TranslateService,
    private datepickerUtils: DatepickerUtilsService
  ) {}

  ngOnInit(): void {
    this.loaderGenericService.showSpinner();

    let dataString: string = localStorage.getItem('credentials');
    this.userInfo = JSON.parse(dataString);

    if (this.userInfo.userType == MACRO_OFFICE) this.isMacroOffices = true;

    if (this.userInfo.userType == MACRO_CHAIN) this.isMacroChains = true;

    if (this.userInfo.userType == BUSINESS_DIRECTOR) this.isBussinesDirector = true;

    this.investorForm = this.formBuilder.group(
      {
        initialDate: ['', [Validators.required, this.customValidators.checkIsFutureDate]],
        finalDate: [{ value: '', disabled: true }, [Validators.required, this.customValidators.checkIsFutureDate]],
        division: ['', Validators.required],
        typeAgent: ['', [Validators.required]],
        agent: [{ value: '', disabled: true }, Validators.required],
        detail: [{ value: '', disabled: true }, Validators.required],
      },
      {
        validators: [
          this.customValidators.isDateValid('initialDate', 'finalDate', 'dateInvalid'),
          this.customValidators.isMoreThanAYearApart('initialDate', 'finalDate', 'dateYearError'),
        ],
      }
    );

    if (this.isMacroChains || this.isBussinesDirector) {
      this.investorForm.controls.typeAgent?.disable();
      this.investorForm.addControl('macroChain', new FormControl('', Validators.required));
      this.investorForm.controls.macroChain.valueChanges.subscribe(() => this.onChangeMacroChain());
      this.getMacroChains();
    }

    if (this.isMacroOffices || this.isBussinesDirector) {
      this.investorForm.controls.typeAgent?.disable();
      this.investorForm.addControl('macroOffice', new FormControl('', Validators.required));
      this.investorForm.controls.macroOffice.valueChanges.subscribe(() => this.onChangeMacroOffice());
      this.getMacroOffices();
    }
    this.translateService.currentLang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan();
    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe((event) =>
        event.lang === LOCALE_ES ? this.datepickerUtils.spanish() : this.datepickerUtils.catalan()
      );

    this.investorForm.controls.initialDate.statusChanges.subscribe((value) => {
      const initialDateValue = this.investorForm.controls.initialDate.value;
      if (value === 'VALID' || initialDateValue) {
        this.minDate = initialDateValue.toDate();
        this.maxFinalDate = moment(initialDateValue).add(11, 'months').endOf('month').toDate();
        if (this.investorForm.controls.finalDate.disabled) this.investorForm.controls.finalDate.enable();
      } else {
        this.minDate = undefined;
        this.maxFinalDate = this.maxDate;
      }
    });

    this.investorForm.controls.typeAgent.valueChanges.subscribe((value) => {
      console.log(this.investorForm);
      this.loaderGenericService.showSpinner();
      this.agentsReportList = null;
      if (value) {
        this.investorForm.controls.agent.enable();
        if (value === AgentType.Group) {
          this.getListGroup();
          if (this.isDetailAvailable) {
            this.investorForm.controls.detail.reset();
            this.investorForm.controls.detail.enable();
          }
        } else if (value === AgentType.NifCif) {
          this.getListNifCif();
          if (this.isDetailAvailable) {
            this.investorForm.controls.detail.reset();
            this.investorForm.controls.detail.enable();
          }
        } else if (value === AgentType.Agent) {
          this.getListAgents();
          if (this.isDetailAvailable) {
            this.investorForm.controls.detail.reset();
            this.investorForm.controls.detail.enable();
          }
        }
      } else {
        this.loaderGenericService.stopSpinner();
      }
    });

    this.agentTypeList = this.controlPanelStore.agentType;
    this.detailsList = this.controlPanelStore.agentDetail;

    this.getListDivision().finally(() => this.loaderGenericService.stopSpinner());
  }

  private getListDivision() {
    return this.combosStore.getDivisions().then(
      (data) => {
        if (data) {
          this.divisionList = data;
          if (this.divisionList.length === 1) {
            this.investorForm.controls.division.setValue(this.divisionList[0].id);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private getListGroup() {
    this.combosStore.getChains(this.getFilter()).then(
      (data) => {
        if (data) {
          this.agentsReportList = data;
          if (this.agentsReportList.length === 1) {
            this.investorForm.controls.agent.setValue(this.agentsReportList[0].id);
          }
        }
        this.loaderGenericService.stopSpinner();
      },
      (error) => console.error(error)
    );
  }

  private getListNifCif() {
    this.combosStore.getPrescribers(this.getFilter()).then(
      (data) => {
        if (data) {
          this.agentsReportList = data;
          if (this.agentsReportList.length === 1) {
            this.investorForm.controls.agent.setValue(this.agentsReportList[0].id);
          }
        }
        this.loaderGenericService.stopSpinner();
      },
      (error) => console.error(error)
    );
  }

  private getListAgents() {
    this.combosStore.getEstablishments(this.getFilter()).then(
      (data) => {
        if (data) {
          this.agentsReportList = data;
          if (this.agentsReportList.length === 1) {
            this.investorForm.controls.agent.setValue(this.agentsReportList[0].id);
          }
        }
        this.loaderGenericService.stopSpinner();
      },
      (error) => console.error(error)
    );
  }

  private getFilter() {
    let filter: any = null;

    if (this.isMacroOffices || (this.isBussinesDirector && this.investorForm.get('macroOffice')?.value)) {
      filter = {
        officeId: this.investorForm.get('macroOffice')?.value,
        userType: OFFICE_DIRECTOR,
      };
    }

    if (this.isMacroChains || (this.isBussinesDirector && this.investorForm.get('macroChain')?.value)) {
      filter = {
        chainId: this.investorForm.get('macroChain')?.value,
        userType: CHAIN,
      };
    }

    return filter;
  }

  consult(): void {
    if (this.investorForm.valid) {
      this.consultEvent.emit({
        form: this.investorForm,
        division: this.divisionList.find((division) => division.id === this.investorForm.controls.division?.value)
          ?.value,
        user: this.investorForm.controls.agent?.value,
      });
    }
  }

  isAgentGenericSelect() {
    return this.agentsReportList?.length < MAX_SELECTOR_DATA;
  }

  /* HANDLERS */

  chosenMonthHandler(normalizedMonth: Moment, datepicker: MatDatepicker<Moment>, input: any) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.investorForm.controls[inputForm];
    const ctrlValue = this.investorForm.controls[inputForm].value;
    if (control.pristine) control.markAsDirty();
    if (ctrlValue) {
      ctrlValue.month(normalizedMonth.month());
      control.setValue(ctrlValue);
    } else {
      control.setValue(
        moment('1/' + (normalizedMonth.month() + 1).toString() + '/' + normalizedMonth.year().toString(), 'DD-MM-YYYY')
      );
    }
    datepicker.close();
  }

  chosenYearHandler(normalizedYear: Moment, input: any) {
    const inputForm = typeof input === 'string' ? input : input?.getAttribute('formControlName');
    const control: AbstractControl = this.investorForm.controls[inputForm];
    const ctrlValue = this.investorForm.controls[inputForm].value;
    if (ctrlValue) {
      ctrlValue.year(normalizedYear.year());
      control.setValue(ctrlValue);
    } else {
      control.setValue(moment('1/1/' + normalizedYear.year().toString(), 'DD-MM-YYYY'));
    }
  }

  private getMacroChains() {
    if (this.isMacroChains) {
      return this.combosStore.getChainsFromMacroUser(this.userInfo.userId).then(
        (data) => {
          if (data) {
            this.macroChainList = data;
            if (this.macroChainList.length === 1) {
              this.investorForm.controls.macroChain.setValue(this.macroChainList[0].id);
            }
            this.macroChainList.unshift({ id: undefined, value: '' });
          }
        },
        (error) => console.error(error)
      );
    } else {
      return this.combosStore.getChainsByDivision(this.userInfo.division?.toString()).then(
        (data) => {
          if (data) {
            this.macroChainList = data;
            if (this.macroChainList.length === 1) {
              this.investorForm.controls.macroChain.setValue(this.macroChainList[0].id);
            }
            this.macroChainList.unshift({ id: undefined, value: '' });
          }
        },
        (error) => console.error(error)
      );
    }
  }
  private getMacroOffices() {
    if (this.isMacroOffices) {
      return this.combosStore.getOfficesFromMacroUser(this.userInfo.userId).then(
        (data) => {
          if (data) {
            this.macroOfficeList = data;
            if (this.macroOfficeList.length === 1) {
              this.investorForm.controls.macroOffice.setValue(this.macroOfficeList[0].id);
            }
            this.macroOfficeList.unshift({ id: undefined, value: '' });
          }
        },
        (error) => console.error(error)
      );
    } else {
      return this.combosStore.getOfficesByDivision(this.userInfo.division?.toString()).then(
        (data) => {
          if (data) {
            this.macroOfficeList = data;
            if (this.macroOfficeList.length === 1) {
              this.investorForm.controls.macroOffice.setValue(this.macroOfficeList[0].id);
            }
            this.macroOfficeList.unshift({ id: undefined, value: '' });
          }
        },
        (error) => console.error(error)
      );
    }
  }

  onChangeMacroOffice() {
    this.investorForm.controls.typeAgent?.setValue(null);
    this.agentsReportList = null;
    this.investorForm.controls.agent?.disable();

    if (this.investorForm.controls.macroOffice.value) {
      this.investorForm.controls.typeAgent?.enable();

      this.investorForm.controls.macroChain?.disable();
      this.investorForm.controls.macroChain?.setValidators(null);
    } else {
      this.investorForm.controls.typeAgent?.disable();

      this.investorForm.controls.macroOffice.setValue(null);
      this.investorForm.controls.macroChain?.setValidators([Validators.required]);
      this.investorForm.controls.macroChain?.enable();
    }
  }

  onChangeMacroChain() {
    this.investorForm.controls.typeAgent?.setValue(null);
    this.agentsReportList = null;
    this.investorForm.controls.agent?.disable();

    if (this.investorForm.controls.macroChain.value) {
      this.investorForm.controls.typeAgent?.enable();

      this.investorForm.controls.macroOffice?.disable();
      this.investorForm.controls.macroOffice?.setValidators(null);
    } else {
      this.investorForm.controls.typeAgent?.disable();

      this.investorForm.controls.macroChain.setValue(null);
      this.investorForm.controls.macroOffice?.setValidators([Validators.required]);
      this.investorForm.controls.macroOffice?.enable();
    }
  }

  isMacroChainMaxSelectorLength = () => this.macroChainList.length < MAX_SELECTOR_DATA;
  isMacroOfficeMaxSelectorLength = () => this.macroOfficeList.length < MAX_SELECTOR_DATA;

  deactivateRequired = () => {
    this.investorForm.controls.typeAgent.setValidators(null);
    this.investorForm.controls.typeAgent.updateValueAndValidity({ onlySelf: true, emitEvent: true });
  };

  activateRequired = () => {
    this.investorForm.controls.typeAgent.setValidators(Validators.required);
    this.investorForm.controls.typeAgent.updateValueAndValidity({ onlySelf: true, emitEvent: true });
  };
}
